import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Clock, Loader2, Save, Plus, Trash2, Coffee, AlertCircle } from "lucide-react";
import TimePicker from "./TimePicker";
import { API } from "@/App";
import { useTranslation } from "react-i18next";

const DAY_VALUES = [
  { value: 1, key: 'monday' },
  { value: 2, key: 'tuesday' },
  { value: 3, key: 'wednesday' },
  { value: 4, key: 'thursday' },
  { value: 5, key: 'friday' },
  { value: 6, key: 'saturday' },
  { value: 0, key: 'sunday' },
];

// Only two break options - translated inside component
const BREAK_PRESETS_RAW = [
  { nameKey: "common:lunchBreak", start_time: "12:00", end_time: "13:00" },
];

// Helper to convert time to minutes
const timeToMinutes = (time) => {
  if (!time) return 0;
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
};

// Validate time range
const isValidTimeRange = (start, end) => {
  return timeToMinutes(start) < timeToMinutes(end);
};

export default function TrainerScheduleModal({ 
  isOpen, 
  onClose, 
  trainer, 
  businessId,
  businessWorkingHours 
}) {
  const { t } = useTranslation(["common", "settings"]);
  const DAYS = DAY_VALUES.map(d => ({ ...d, label: t(`common:${d.key}`) }));
  const BREAK_PRESETS = BREAK_PRESETS_RAW.map(b => ({ ...b, name: t(b.nameKey) }));
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [schedules, setSchedules] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isOpen && trainer) {
      fetchSchedule();
      setErrors({});
    }
  }, [isOpen, trainer]);

  const fetchSchedule = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API}/trainers/${trainer.trainer_id}/schedule?business_id=${businessId}`,
        { credentials: "include" }
      );
      if (response.ok) {
        const data = await response.json();
        const scheduleMap = {};
        data.forEach((s) => {
          scheduleMap[s.day_of_week] = {
            start_time: s.start_time,
            end_time: s.end_time,
            is_active: s.is_active !== false,
            breaks: s.breaks || [],
          };
        });
        setSchedules(scheduleMap);
      }
    } catch (error) {
      console.error("Error fetching schedule:", error);
    } finally {
      setLoading(false);
    }
  };

  // Validate all schedules and breaks
  const validateSchedules = () => {
    const newErrors = {};
    let hasError = false;

    Object.entries(schedules).forEach(([dayValue, schedule]) => {
      if (!schedule) return;

      const dayErrors = { main: false, breaks: [], outsideBusiness: false };

      // Validate main working hours
      if (!isValidTimeRange(schedule.start_time, schedule.end_time)) {
        dayErrors.main = true;
        hasError = true;
      }

      // Check if trainer hours are within business hours
      if (businessWorkingHours) {
        const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        const dayName = dayNames[parseInt(dayValue)];
        const businessDay = businessWorkingHours[dayName];
        
        if (businessDay && !businessDay.closed) {
          const businessOpenMins = timeToMinutes(businessDay.open);
          const businessCloseMins = timeToMinutes(businessDay.close);
          const trainerStartMins = timeToMinutes(schedule.start_time);
          const trainerEndMins = timeToMinutes(schedule.end_time);
          
          if (trainerStartMins < businessOpenMins || trainerEndMins > businessCloseMins) {
            dayErrors.outsideBusiness = true;
            hasError = true;
          }
        }
      }

      // Validate breaks
      if (schedule.breaks) {
        schedule.breaks.forEach((brk, idx) => {
          if (!isValidTimeRange(brk.start_time, brk.end_time)) {
            dayErrors.breaks[idx] = true;
            hasError = true;
          }
        });
      }

      if (dayErrors.main || dayErrors.outsideBusiness || dayErrors.breaks.length > 0) {
        newErrors[dayValue] = dayErrors;
      }
    });

    setErrors(newErrors);
    return !hasError;
  };

  const handleDayToggle = (dayValue, enabled) => {
    setSchedules((prev) => ({
      ...prev,
      [dayValue]: enabled
        ? { start_time: "09:00", end_time: "18:00", is_active: true, breaks: [] }
        : undefined,
    }));
    // Clear errors for this day
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[dayValue];
      return newErrors;
    });
  };

  const handleTimeChange = (dayValue, field, value) => {
    setSchedules((prev) => {
      const newSchedule = {
        ...prev[dayValue],
        [field]: value,
      };
      // Validate immediately with new value
      validateDayWithSchedule(dayValue, newSchedule);
      return {
        ...prev,
        [dayValue]: newSchedule,
      };
    });
  };

  const validateDay = (dayValue) => {
    setSchedules((prev) => {
      const schedule = prev[dayValue];
      if (schedule) {
        validateDayWithSchedule(dayValue, schedule);
      }
      return prev;
    });
  };

  const validateDayWithSchedule = (dayValue, schedule) => {
    if (!schedule) return;

    const dayErrors = { main: false, breaks: [], outsideBusiness: false };

    // Check if time range is valid (start < end)
    if (!isValidTimeRange(schedule.start_time, schedule.end_time)) {
      dayErrors.main = true;
    }

    // Check if trainer hours are within business hours
    if (businessWorkingHours) {
      const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      const dayName = dayNames[parseInt(dayValue)];
      const businessDay = businessWorkingHours[dayName];
      
      if (businessDay && !businessDay.closed) {
        const businessOpenMins = timeToMinutes(businessDay.open);
        const businessCloseMins = timeToMinutes(businessDay.close);
        const trainerStartMins = timeToMinutes(schedule.start_time);
        const trainerEndMins = timeToMinutes(schedule.end_time);
        
        if (trainerStartMins < businessOpenMins || trainerEndMins > businessCloseMins) {
          dayErrors.outsideBusiness = true;
        }
      }
    }

    // Validate breaks
    if (schedule.breaks) {
      schedule.breaks.forEach((brk, idx) => {
        if (!isValidTimeRange(brk.start_time, brk.end_time)) {
          dayErrors.breaks[idx] = true;
        }
      });
    }

    setErrors((prev) => {
      if (!dayErrors.main && !dayErrors.outsideBusiness && dayErrors.breaks.every(b => !b)) {
        const newErrors = { ...prev };
        delete newErrors[dayValue];
        return newErrors;
      }
      return { ...prev, [dayValue]: dayErrors };
    });
  };

  const addBreak = (dayValue, preset = null) => {
    const newBreak = preset || { name: "", start_time: "12:00", end_time: "13:00" };
    setSchedules((prev) => ({
      ...prev,
      [dayValue]: {
        ...prev[dayValue],
        breaks: [...(prev[dayValue]?.breaks || []), { ...newBreak }],
      },
    }));
  };

  const updateBreak = (dayValue, breakIndex, field, value) => {
    setSchedules((prev) => {
      const breaks = [...(prev[dayValue]?.breaks || [])];
      breaks[breakIndex] = { ...breaks[breakIndex], [field]: value };
      const newSchedule = {
        ...prev[dayValue],
        breaks,
      };
      // Validate immediately with new value
      validateDayWithSchedule(dayValue, newSchedule);
      return {
        ...prev,
        [dayValue]: newSchedule,
      };
    });
  };

  const removeBreak = (dayValue, breakIndex) => {
    setSchedules((prev) => ({
      ...prev,
      [dayValue]: {
        ...prev[dayValue],
        breaks: prev[dayValue]?.breaks?.filter((_, i) => i !== breakIndex) || [],
      },
    }));
    // Clear break error
    setErrors((prev) => {
      if (prev[dayValue]) {
        const newBreaks = [...(prev[dayValue].breaks || [])];
        newBreaks.splice(breakIndex, 1);
        return {
          ...prev,
          [dayValue]: { ...prev[dayValue], breaks: newBreaks }
        };
      }
      return prev;
    });
  };

  const handleSave = async () => {
    // Validate before save
    if (!validateSchedules()) {
      toast.error(t("common:toast_lutfenHataliSaatleriDuzeltinBaslangic"));
      return;
    }

    setSaving(true);
    try {
      const scheduleList = Object.entries(schedules)
        .filter(([_, val]) => val && val.start_time && val.end_time)
        .map(([day, val]) => ({
          day_of_week: parseInt(day),
          start_time: val.start_time,
          end_time: val.end_time,
          is_active: val.is_active !== false,
          breaks: val.breaks || [],
        }));

      const response = await fetch(
        `${API}/trainers/${trainer.trainer_id}/schedule`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            business_id: businessId,
            schedules: scheduleList,
          }),
        }
      );

      if (response.ok) {
        toast.success(t('settings:workingHoursSaved'));
        onClose();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('common:errorOccurred'));
      }
    } catch (error) {
      toast.error(t('common:errorOccurred'));
    } finally {
      setSaving(false);
    }
  };

  const getBusinessHoursForDay = (dayValue) => {
    if (!businessWorkingHours) return null;
    const dayNames = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
    return businessWorkingHours[dayNames[dayValue]];
  };

  const hasAnyError = Object.keys(errors).length > 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-navy" />
            {t("common:workingHoursTitle")} - {trainer?.name} {trainer?.surname}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-navy" />
          </div>
        ) : (
          <div className="space-y-4 mt-4">
            <p className="text-sm text-grey">
              {t("common:workingHoursDesc")}
            </p>

            {hasAnyError && (
              <div className="space-y-2">
                {Object.values(errors).some(e => e.main) && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-xl flex items-center gap-2 text-red-600 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    {t("common:startBeforeEnd")}
                  </div>
                )}
                {Object.values(errors).some(e => e.outsideBusiness) && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-2 text-amber-700 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    {t("common:outsideBusinessHours")}
                  </div>
                )}
              </div>
            )}

            {DAYS.map((day) => {
              const schedule = schedules[day.value];
              const businessHours = getBusinessHoursForDay(day.value);
              const isEnabled = !!schedule;
              const dayError = errors[day.value];

              return (
                <div
                  key={day.value}
                  className={`p-4 rounded-xl border ${
                    dayError?.main ? "border-red-300 bg-red-50" :
                    dayError?.outsideBusiness ? "border-amber-300 bg-amber-50" :
                    isEnabled ? "bg-slate-50 border-navy/20" : "bg-grey-50 border-grey-200"
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <Switch
                        checked={isEnabled}
                        onCheckedChange={(checked) => handleDayToggle(day.value, checked)}
                      />
                      <Label className={`font-medium ${!isEnabled && "text-grey"}`}>
                        {day.label}
                      </Label>
                    </div>
                    {businessHours && !businessHours.closed && (
                      <span className="text-xs text-grey bg-white px-2 py-1 rounded">
                        {t("common:businessHours", { open: businessHours.open, close: businessHours.close })}
                      </span>
                    )}
                    {businessHours?.closed && (
                      <span className="text-xs text-red-500 bg-red-50 px-2 py-1 rounded">
                        {t("common:businessClosed")}
                      </span>
                    )}
                  </div>

                  {/* Outside business hours warning */}
                  {dayError?.outsideBusiness && (
                    <div className="mb-3 p-2 bg-amber-100 border border-amber-300 rounded-lg text-xs text-amber-700 flex items-center gap-2">
                      <AlertCircle className="w-3 h-3" />
                      {t("common:outsideBusinessHoursWarning", { open: businessHours?.open, close: businessHours?.close })}
                    </div>
                  )}

                  {isEnabled && (
                    <div className="space-y-3">
                      {/* Working Hours */}
                      <div className={`grid grid-cols-2 gap-3 p-2 rounded-lg ${dayError?.main ? "bg-red-100" : ""}`}>
                        <div>
                          <Label className="text-xs text-grey">{t('settings:startTime')}</Label>
                          <TimePicker
                            value={schedule?.start_time || "09:00"}
                            onChange={(val) => handleTimeChange(day.value, "start_time", val)}
                            minHour={businessHours ? parseInt(businessHours.open.split(":")[0]) : 6}
                            maxHour={businessHours ? parseInt(businessHours.close.split(":")[0]) : 23}
                            minuteStep={15}
                          />
                        </div>
                        <div>
                          <Label className="text-xs text-grey">{t('settings:endTime')}</Label>
                          <TimePicker
                            value={schedule?.end_time || "18:00"}
                            onChange={(val) => handleTimeChange(day.value, "end_time", val)}
                            minHour={businessHours ? parseInt(businessHours.open.split(":")[0]) : 6}
                            maxHour={businessHours ? parseInt(businessHours.close.split(":")[0]) : 23}
                            minuteStep={15}
                          />
                        </div>
                      </div>

                      {/* Breaks */}
                      {schedule?.breaks?.length > 0 && (
                        <div className="space-y-2">
                          <Label className="text-xs text-grey flex items-center gap-1">
                            <Coffee className="w-3 h-3" /> {t("common:breaks")}
                          </Label>
                          {schedule.breaks.map((brk, idx) => {
                            const hasBreakError = dayError?.breaks?.[idx];
                            return (
                              <div 
                                key={idx} 
                                className={`p-3 rounded-lg border ${hasBreakError ? "bg-red-100 border-red-300" : "bg-white"}`}
                              >
                                <div className="flex items-center gap-2 mb-2">
                                  <Input
                                    className="flex-1 h-8 text-sm rounded-lg"
                                    placeholder={t("common:ph_molaAdi")}
                                    value={brk.name}
                                    onChange={(e) => updateBreak(day.value, idx, "name", e.target.value)}
                                  />
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-red-600 hover:bg-red-50"
                                    onClick={() => removeBreak(day.value, idx)}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                  <div>
                                    <Label className="text-xs text-grey">{t('settings:startTime')}</Label>
                                    <TimePicker
                                      value={brk.start_time}
                                      onChange={(val) => updateBreak(day.value, idx, "start_time", val)}
                                      minHour={6}
                                      maxHour={23}
                                      minuteStep={15}
                                    />
                                  </div>
                                  <div>
                                    <Label className="text-xs text-grey">{t('settings:endTime')}</Label>
                                    <TimePicker
                                      value={brk.end_time}
                                      onChange={(val) => updateBreak(day.value, idx, "end_time", val)}
                                      minHour={6}
                                      maxHour={23}
                                      minuteStep={15}
                                    />
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Add Break Buttons */}
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs h-8 rounded-lg"
                          onClick={() => addBreak(day.value, BREAK_PRESETS[0])}
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          {t("common:lunchBreak")}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-xs h-8 rounded-lg"
                          onClick={() => addBreak(day.value)}
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          {t("common:customBreak")}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                className="flex-1 rounded-xl"
                onClick={onClose}
              >
                {t("common:cancel")}
              </Button>
              <Button
                className={`flex-1 rounded-xl ${hasAnyError ? "bg-grey-400" : "bg-navy hover:bg-navy/90"}`}
                onClick={handleSave}
                disabled={saving || hasAnyError}
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t("common:saving")}
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    {t("common:save")}
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
